import { useColorMode } from '@chakra-ui/react';
import Image from 'next/image';
import Link from 'next/link';

export function NextjsQuickstartHero() {
  const { colorMode } = useColorMode();
  const isLightMode = colorMode === 'light';

  return (
    <>
      <Image
        src={
          isLightMode
            ? '/images/nextjs-clerk-logo-light.svg'
            : '/images/nextjs-clerk-logo-dark.svg'
        }
        alt="Next.js's logo"
        width={228}
        height={64}
      />

      <h1 className="mt-14 text-[2rem]/10 font-semibold tracking-tight">
        Add authentication and user management to your Next.js app with Clerk
      </h1>

      <div className="justify-between mt-11 sm:flex">
        <div className="space-y-4">
          <h3 className="text-2xl font-semibold tracking-tight">
            You will learn how to:
          </h3>

          <ul className="list-none space-y-1 text-[#5E5F6E] marker:text-[#D9D9DE] dark:text-[#D9D9DE] dark:marker:text-[#42434D] lg:list-disc">
            <li>
              Install <code>@clerk/nextjs</code>
            </li>
            <li>Set up your environment keys</li>
            <li>
              Wrap your Next.js app in <code>{`<ClerkProvider/>`}</code>
            </li>
            <li>Limit access to authenticated users</li>
            <li>
              Embed the <code>{`<UserButton/>`}</code>
            </li>
          </ul>
        </div>

        <div className="mt-11 space-y-2.5 sm:mt-12">
          <h4 className="text-[0.9375rem]/[1.125rem] font-medium text-[#9394A1]">
            Prerequisites
          </h4>

          <ul className="space-y-2 sm:space-y-1">
            <li>
              <Link
                href="https://clerk.com/docs/quickstarts/setup-clerk"
                target="_blank"
                className="tracking-tight text-[#6C47FF] underline underline-offset-4 dark:text-[#9785FF]"
              >
                Set up Clerk
              </Link>
            </li>
            <li>
              <Link
                href="https://nextjs.org/docs/getting-started/installation#automatic-installation"
                target="_blank"
                className="tracking-tighter text-[#6C47FF] underline underline-offset-4 dark:text-[#9785FF]"
              >
                A Next.js application
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="my-10">
        <div className="flex justify-center">
          <span className="translate-y-2 bg-white px-3 text-[0.8125rem]/[1.125rem] font-semibold text-[#9394A1] dark:bg-[#1A1B1E] dark:text-white">
            Example quickstart repos
          </span>
        </div>

        <div className="flex flex-col items-center gap-4 rounded-3xl border border-gray-700/5 p-4 dark:border-white/[0.04] sm:flex-row sm:rounded-full">
          <Link
            href="https://github.com/clerkinc/clerk-nextjs-app-quickstart"
            target="_blank"
            className="inline-flex w-full flex-1 items-center justify-center gap-2 rounded-lg bg-[#F7F7F8] py-3 font-semibold text-white shadow-[inset_0px_0px_0px_1px_rgb(0_0_0/0.02),inset_0px_1px_0px_rgb(0_0_0/0.03)] dark:bg-[#212126] dark:shadow-[inset_0px_0px_0px_1px_rgb(255_255_255/0.02),inset_0px_1px_0px_rgb(255_255_255/0.04)] sm:rounded-full sm:py-[1.125rem]"
          >
            <Image
              src="/images/app-router-icon.svg"
              alt="Blue cube icon"
              width={22}
              height={26}
            />
            <p className="whitespace-nowrap text-xs text-[#131316] dark:text-white sm:text-[0.9375rem]/[1.125rem]">
              App <span className="text-[#9394A1]">router</span>
            </p>
          </Link>

          <Image
            src={
              isLightMode
                ? '/images/github-gradient.svg'
                : '/images/github-gradient-dark.svg'
            }
            alt="GitHub Logo with Gradient"
            width={48}
            height={48}
            className="hidden sm:block"
          />

          <Link
            href="https://github.com/clerkinc/clerk-nextjs-pages-quickstart"
            target="_blank"
            className="inline-flex w-full flex-1 items-center justify-center gap-2 rounded-lg bg-[#F7F7F8] py-3 font-semibold text-white shadow-[inset_0px_0px_0px_1px_rgb(0_0_0/0.02),inset_0px_1px_0px_rgb(0_0_0/0.03)] dark:bg-[#212126] dark:shadow-[inset_0px_0px_0px_1px_rgb(255_255_255/0.02),inset_0px_1px_0px_rgb(255_255_255/0.04)] sm:rounded-full sm:py-[1.125rem]"
          >
            <Image
              src="/images/pages-router-icon.svg"
              alt="Purple pages icon"
              width={21}
              height={25}
            />
            <p className="whitespace-nowrap text-xs text-[#131316] dark:text-white sm:text-[0.9375rem]/[1.125rem]">
              Pages <span className="text-[#9394A1]">router</span>
            </p>
          </Link>
        </div>
      </div>
    </>
  );
}
